import { CKEditor } from 'ckeditor4-react';
import { useEffect, useRef, useState } from 'react';

// ckeditor4-react recreates the editor instance whenever a new `config`
// reference is passed. Keeping this object module-level guarantees referential
// equality across renders so the editor is NOT destroyed while a dialog (e.g.
// Link) is open, which previously left an orphaned modal cover that greyed out
// and froze the page.
const editorConfig = {
    // Raise CKEditor's floating layers (dialogs, dropdown panels) above Fluent UI
    // Panels/Layers (~1,000,000). Using baseFloatZIndex lets CKEditor keep the dialog
    // above its own background cover; forcing these via CSS !important put the cover
    // on top of the dialog buttons and swallowed all clicks (link dialog froze the page).
    baseFloatZIndex: 2000000,
    enterMode: 1,
    shiftEnterMode: 2,
    // Editor-side filtering is intentionally disabled so authors can paste rich
    // content (images, styled blocks, etc.).
    allowedContent: true,
    // On Blink/WebKit browsers (Chrome/Edge/Safari) CKEditor defaults pasteFilter
    // to 'semantic-content', which strips <img> and inline styles on paste even
    // when ACF is disabled. null turns off paste-time filtering entirely.
    pasteFilter: null,
    autoUpdateElement: false,
    format_tags: 'p;h2;h3;h4;h5;h6;div',
    toolbar: [
        { name: 'basicstyles', items: ['Bold', 'Italic', 'Strike'] },
        { name: 'links', items: ['Link', 'Unlink'] },
        { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
        { name: 'tools', items: ['Source'] },
        { name: 'styles', items: ['Format'] },
    ],
};

const TextCKEditor = ({ value, onChange }) => {
    const editorRef = useRef(null);
    const [editorReady, setEditorReady] = useState(false);
    const didSetInitialValueRef = useRef(false);
    const initialValueRef = useRef(value);

    // Keep the latest onChange in a ref so the native source-mode listener,
    // which is bound once on instance ready, always calls the current handler.
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (
            editorReady &&
            editorRef.current &&
            !didSetInitialValueRef.current
        ) {
            if (value) {
                editorRef.current.setData(value);
            }
            didSetInitialValueRef.current = true;
        }
    }, [editorReady, value]);

    return (
        <CKEditor
            onInstanceReady={({ editor }) => {
                editorRef.current = editor;
                setEditorReady(true);

                // While in Source view, CKEditor does NOT fire its `change`
                // event, so typed/pasted edits never reach Formik via onChange.
                // The source editable's getData() returns the live textarea
                // value, so bind a native `input` listener each time we enter
                // source mode to keep the form in sync. attachListener is
                // auto-cleaned when the editable is detached on mode switch.
                editor.on('mode', () => {
                    if (editor.mode !== 'source') {
                        return;
                    }
                    const editable = editor.editable();
                    if (!editable) {
                        return;
                    }
                    editable.attachListener(editable, 'input', () => {
                        if (didSetInitialValueRef.current) {
                            onChangeRef.current(editor.getData());
                        }
                    });
                });
            }}
            onChange={({ editor }) => {
                // Only trigger onChange after initial value has been set
                // This prevents marking the form as dirty during initialization
                if (didSetInitialValueRef.current) {
                    onChange(editor.getData());
                }
            }}
            onBlur={({ editor }) => {
                // Sync data on blur so Formik has current values before form submit
                if (didSetInitialValueRef.current) {
                    onChange(editor.getData());
                }
            }}
            onMode={({ editor }) => {
                // Sync data when switching between source and WYSIWYG modes
                if (didSetInitialValueRef.current) {
                    onChange(editor.getData());
                }
            }}
            config={editorConfig}
        />
    );
};

export default TextCKEditor;
